import { Injectable } from "@angular/core";
import { HttpClient, HttpHeaders, HttpParams } from "@angular/common/http";
import { catchError, Observable, of, switchMap, tap } from "rxjs";
import { User } from "../user/user.types";
import { AppConstants } from "../constants/constants";
import { UserState } from './../user/state/user.state';
import { BusinessAccountState } from "src/app/pages/business-account/state/business-account.state";
import { ImageCacheService } from "../services/image-cache.service";
import { LongRunningRequestService } from "../services/long-running-request.service";
import { AuthUtils } from "./auth.utils";

@Injectable()
export class AuthService {
    private _authenticated: boolean = false;
    public _resetPassVariableFlow: any = {};
    public _signUpVariableFlow: any = {};
    public _emailVerificationInfoVariable: any = {};
    public _verificationState: any = {}
    public _verifyNewDeviceVariableFlow: any = {}
    public _inviteVariableFlow: any = {}
    /**
     * Constructor
     */
    constructor(
        private readonly _httpClient: HttpClient,
        private readonly _imageCacheService: ImageCacheService,
        private readonly _longRunningRequestService: LongRunningRequestService
    ) {
        //get reset data from session storge
        let storedResetData: any = JSON.parse(
            sessionStorage.getItem(AppConstants.PRISISTED_KEYS.RESET_PASS)
        );
        if (storedResetData) this._resetPassVariableFlow = storedResetData;

        //get invitation sign up data from session storage (used by invitation flow)
        let storedsignUpData: any = JSON.parse(
            localStorage.getItem(AppConstants.PRISISTED_KEYS.SIGNUP_DATA)
        );
        if (storedsignUpData) this._signUpVariableFlow = storedsignUpData;

        // get userId in case new device login 
        let storedNewDeviceData: string = sessionStorage.getItem('userId')
        if (storedNewDeviceData) this._verifyNewDeviceVariableFlow.userId = storedNewDeviceData;

    }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------
    /**
     * Setter & getter for access token
     * @param token
     */
    set accessToken(token: string) {
        localStorage.setItem(AppConstants.PRISISTED_KEYS.ACCESS_TOKEN, token);
    }

    get accessToken(): string {
        return localStorage.getItem(AppConstants.PRISISTED_KEYS.ACCESS_TOKEN) ?? "";
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Sign in
     *
     * @param credentials
     * @param macAddress
     */

    signIn(credentials: any, macAddress: string): Observable<User> {
        const url = `${AppConstants.API.USERS_API}/auth`;
        const body = new HttpParams()
            .set("userName", credentials.email)
            .set("password", credentials.password)
            .set('macAddress', macAddress)
        // .set('sourceChannel', 'ADMINISTRATION_PORTAL')

        return this._httpClient
            .post(url, body.toString(), {
                headers: new HttpHeaders().set(
                    "Content-Type", "application/x-www-form-urlencoded"
                ),
            }).pipe(
                tap((res: any) => {
                    // Store the access token in the local storage
                    this.accessToken = res.accessToken;
                    // Set the authenticated flag to true
                    this._authenticated = true;
                    // Store the user on the user state
                    UserState.setUserState(res)
                    return of(true)
                })
            );
    }

    /**
     * Sign out
     * Calls backend API to invalidate token, then clears local state
     */
    signOut(): Observable<boolean> {
        const url = `${AppConstants.API.USERS_API}/logout`;

        // Call backend to invalidate the token
        return this._httpClient.post(url, {}).pipe(
            // Always clear local state, regardless of API success/failure
            catchError(() => {
                // If API fails, still proceed with local cleanup
                return of(null);
            }),
            switchMap(() => {
                // Clear local authentication state
                this.clearLocalAuthState();
                return of(true);
            })
        );
    }

    /**
     * Clear all local authentication state without calling backend API
     * Use this when token is already invalid (e.g., 401 response)
     */
    clearLocalAuthState(): void {
        // Remove the access token and the user from the local storage
        localStorage.removeItem(AppConstants.PRISISTED_KEYS.ACCESS_TOKEN);
        localStorage.removeItem(AppConstants.PRISISTED_KEYS.CURRENT_USER);
        localStorage.removeItem(AppConstants.PRISISTED_KEYS.USER_PERMISSIONS);
        localStorage.removeItem(AppConstants.PRISISTED_KEYS.ACCOUNT_STATUS);
        localStorage.removeItem(AppConstants.PRISISTED_KEYS.MODULE_STATUSES);
        // Clear inactivity cross-tab sync timestamp
        localStorage.removeItem(AppConstants.PRISISTED_KEYS.USER_ACTIVITY_TIMESTAMP);
        // Clear the Business Account State
        BusinessAccountState.clearBusinessAccountState();
        // Clear image cache to free memory
        this._imageCacheService.clearAllImages();
        // Drop long-running-request tracking + hide any visible "still processing"
        // notice — the service is root-scoped and logout doesn't reload the page,
        // so otherwise this state would carry into the next session in this tab.
        this._longRunningRequestService.reset();
        // Clear all auth flow variables
        this.clearAuthFlowVariables();
        // Set the authenticated flag to false
        this._authenticated = false;
    }

    /**
     * Clear all authentication flow variables and their session/local storage
     * Call this after successful verification or on logout
     */
    clearAuthFlowVariables(): void {
        // Clear in-memory variables
        this._resetPassVariableFlow = {};
        this._signUpVariableFlow = {};
        this._verifyNewDeviceVariableFlow = {};
        this._inviteVariableFlow = {};
        this._verificationState = {};
        this._emailVerificationInfoVariable = {};

        // Clear session storage
        sessionStorage.removeItem(AppConstants.PRISISTED_KEYS.RESET_PASS);
        sessionStorage.removeItem('inviteVariableFlow');
        sessionStorage.removeItem('userId');

        // Clear local storage for signup data
        localStorage.removeItem(AppConstants.PRISISTED_KEYS.SIGNUP_DATA);
    }


    /**
 * invitation SignUp
 *
 * @param user
 * @param macAddress
 */

    invitationSignUp(user: User, macAddress: string, token: string): Observable<User> {

        const url = `${AppConstants.API.BUSINESS_ACCOUNT_USERS}?token=${token}`;
        const body = new HttpParams()
            .set("fullName", `${user.firstName} ${user.lastName}`)
            .set("mobileNumber", user.countryCode + user.mobileNumber)
            .set("password", user.password)
            .set("confirmPassword", user.cpassword)
            .set('macAddress', macAddress)

        return this._httpClient
            .post(url, body, {
                headers: new HttpHeaders().set(
                    "Content-Type",
                    "application/x-www-form-urlencoded"
                ),
            })
            .pipe(
                switchMap((response: any) => {
                    // store mobile number in signUp Data variable
                    this._signUpVariableFlow.mobileNumber = user.mobileNumber;
                    //store regestration request id in signUp Data variable
                    this._signUpVariableFlow.signUpRegRequestId = response.regRequestId;
                    localStorage.setItem(AppConstants.PRISISTED_KEYS.SIGNUP_DATA, JSON.stringify(this._signUpVariableFlow));
                    return of(response);
                })
            );

    }

    /**
* invitation SignUp
*
* @param token
*/

    claimTokenInfo(token: string) {
        const url = `${AppConstants.API.CLAIM_TOKEN_API}/${token}`
        return this._httpClient.get(url)
    }

    /**
     * Check the authentication status
     */
    check(): Observable<boolean> {
        // Check the access token availability first
        if (!this.accessToken) {
            this._authenticated = false;
            return of(false);
        }

        // Check the access token expire date BEFORE checking _authenticated flag
        // Add 60 second buffer to logout user before token actually expires
        // This prevents edge cases where token expires mid-request
        if (AuthUtils.isTokenExpired(this.accessToken, 60)) {
            // Token expired - call backend logout API and clear all authentication data
            return this.signOut().pipe(
                switchMap(() => of(false))
            );
        }

        // Check if the user is logged in
        if (this._authenticated) {
            return of(true);
        }

        // If the access token exists and it didn't expire, sign in using it
        return of(true);
    }

    /**
     * Forgot password
     *
     * @param email
     */
    forgotPassword(email: User): Observable<boolean> {
        const url = `${AppConstants.API.USERS_API}/forgotPassword`;
        return this._httpClient.post<boolean>(url, email).pipe(
            switchMap((response: any) => {
                // store email in collect Data Service
                this._resetPassVariableFlow.email = email.email;
                sessionStorage.setItem(AppConstants.PRISISTED_KEYS.RESET_PASS, JSON.stringify(this._resetPassVariableFlow));
                return of(response);
            })
        );
    }

    /**
     * Reset password
     *
     * @param resetPassForm
     */
    resetPassword(resetPassForm: any, resetToken: string): Observable<any> {
        const url = `${AppConstants.API.USERS_API}/resetPassword`;
        const body = new HttpParams()
            .set("password", resetPassForm.password)
            .set("confirmedPassword", resetPassForm.cpassword);

        return this._httpClient.post(url, body, {
            headers: new HttpHeaders()
                .set("Content-Type", "application/x-www-form-urlencoded")
                .set("token", resetToken),
        });
    }

    //confirmOtp

    /**
 * Reset password
 *
 * @param otp
 */
    confirmOtp(otp: string): Observable<any> {
        let url
        if (this._verificationState.verificationType == 'invite-email') {
            //send otp to email coming from invitation registration
            url = `${AppConstants.API.USERS_REGESTRATION_API}/${this._inviteVariableFlow.inviteRegRequestId}/otp/${otp}`;
        } else if (this._verificationState.verificationType == 'signup-mobile') {
            //send otp to mobile number coming from registration
            url = `${AppConstants.API.USERS_REGESTRATION_API}/${this._signUpVariableFlow.signUpRegRequestId}/otp/${otp}`;
        } else if (this._verificationState.verificationType == 'reset-pass') {
            //send otp to email coming from forgot password
            url = `${AppConstants.API.USERS_API}/${this._resetPassVariableFlow.userId}/otp/${otp}/status`;
            return this._httpClient.get(url, {})
        } else {
            //verify otp sent to email to verify new macAddress coming from new device
            url = `${AppConstants.API.USERS_API}/${this._verifyNewDeviceVariableFlow.userId}/otp/${otp}`;
        }
        //auto login if coming from registration or new device

        if (this._verificationState.verificationType == 'newDevice') {
            return this._httpClient.post(url, {}).pipe(
                tap((res: any) => {
                    // Store the access token in the local storage
                    this.accessToken = res.accessToken;
                    // Set the authenticated flag to true
                    this._authenticated = true;
                    // Store the user on the user state
                    localStorage.setItem(AppConstants.PRISISTED_KEYS.ACCOUNT_STATUS, res.manageAccounts[0].status.value);
                    UserState.setUserState(res)

                    return of(true)
                })
            );
        } else {
            return this._httpClient.post(url, {})
        }
    }

    /**
     * Get the appropriate identifier for OTP resend based on verification type
     * @returns The identifier (email, mobile number, or userId) based on current verification flow
     */
    getResendOtpIdentifier(): string {
        switch (this._verificationState.verificationType) {
            case 'signup-mobile':
                return this._signUpVariableFlow.mobileNumber || '';
            case 'newDevice':
                return this._verifyNewDeviceVariableFlow.userId || '';
            case 'invite-email':
                return this._inviteVariableFlow.email || '';
            default:
                return '';
        }
    }

    /**
     * resend Otp
     * @param id
     */
    resendOtp(id: string): Observable<any> {
        let url
        if (this._verificationState.verificationType == 'invite-email') {
            //resend otp to email for invitation
            url = `${AppConstants.API.USERS_API}/registration/${this._inviteVariableFlow.inviteRegRequestId}/otp/resend`;
        } else if (this._verificationState.verificationType == 'signup-mobile') {
            //resend otp to mobile number
            url = `${AppConstants.API.USERS_API}/registration/${this._signUpVariableFlow.signUpRegRequestId}/otp/resend`;
        } else {
            //resend otp to email
            url = `${AppConstants.API.USERS_API}/${id}/otp/resend`;
        }
        return this._httpClient.get(url, {});
    }

    /**
 * Resend Verification Email
 */
    resendVerificationEmail() {
        let url = `${AppConstants.API.USERS_REGESTRATION_API}/${this._signUpVariableFlow.signUpRegRequestId}/email/resend`;
        return this._httpClient.post(url, {})
    }


    /**
     * activate Account
     * @param token
     */
    activateAccount(token: string): Observable<any> {
        const url = `${AppConstants.API.USERS_REGESTRATION_API}/email/verification?token=${token}`;
        return this._httpClient.post(url, {}, {
            headers: new HttpHeaders()
                .set('Content-Type', 'application/x-www-form-urlencoded')
        })
    }
}
